import React, { useState, useEffect, useCallback } from "react";
import { JsonRpc } from "@proton/js";
import { normalizeTimestamp, formatDate } from "../utils/dateUtils";
import { fetchOutcomeNamesForMarket } from "../utils/outcomes";
import CommentReactions, {
  ReactionData,
  DEFAULT_REACTIONS,
} from "./CommentReactions";
import Loader from "./loader/Loader";
import { editMarketTransaction } from "../utils/crypto";

interface Comment {
  id: number;
  market_id: number;
  user_account: string;
  comment_text: string;
  created_at: number;
  updated_at: number | null;
  parent_comment_id: number | null;
  is_deleted: number;
  deleted_by: string | null;
  deleted_at: number | null;
}

interface MarketProposal {
  id: number;
  question: string;
  category: string;
  image_url: string;
  expireSec: number;
  suggested_by: string;
  isLive: boolean;
  resolved: boolean;
  outcomes: string[];
  outcomes_count: number;
  approvalCount: number;
  isDeclined: boolean;
  declineComment: string;
  declinedBy: string;
  description?: string;
  resolution_criteria?: string;
}

interface MyProposalsProps {
  session: any;
  initialMarketId?: number | null;
  onBackToList?: () => void;
  onNavigateToDetail?: (marketId: number) => void;
}

const MyProposals: React.FC<MyProposalsProps> = ({
  session,
  initialMarketId,
  onBackToList,
  onNavigateToDetail,
}) => {
  const [proposals, setProposals] = useState<MarketProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editResolutionCriteria, setEditResolutionCriteria] = useState("");
  const [editImageUrl, setEditImageUrl] = useState("");
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState("");
  const [editUploadingImage, setEditUploadingImage] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [cardComments, setCardComments] = useState<Record<number, Comment[]>>(
    {},
  );

  // Detail view state
  const [selectedProposalId, setSelectedProposalId] = useState<number | null>(
    null,
  );
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState<Comment | null>(null);
  const [commentAdmins, setCommentAdmins] = useState<string[]>([]);
  const [commentReactions, setCommentReactions] = useState<
    Record<number, Record<string, ReactionData>>
  >({});
  const [userCommentReactions, setUserCommentReactions] = useState<
    Record<number, string | null>
  >({});

  // Edit comment state
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [editCommentLoading, setEditCommentLoading] = useState(false);

  const showToast = (
    message: string,
    type: "success" | "error" = "success",
  ) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Auto-open detail view when initialMarketId prop is provided (e.g. from notification link)
  useEffect(() => {
    if (initialMarketId != null) {
      setSelectedProposalId(initialMarketId);
    }
  }, [initialMarketId]);

  const fetchDeclineRecord = useCallback(
    async (
      rpc: JsonRpc,
      marketId: number,
    ): Promise<{
      isDeclined: boolean;
      declineComment: string;
      declinedBy: string;
    }> => {
      try {
        const contractName =
          process.env.REACT_APP_CONTRACT_NAME || "xpredict";
        const result = await rpc.get_table_rows({
          json: true,
          code: contractName,
          scope: contractName,
          table: "mktdecline",
          lower_bound: marketId,
          upper_bound: marketId,
          limit: 1,
        });
        if (result.rows.length > 0) {
          const row = result.rows[0];
          return {
            isDeclined: true,
            declineComment: row.decline_comment || "",
            declinedBy: row.declined_by || "",
          };
        }
      } catch (error) {
        console.error(
          `Error fetching decline record for market ${marketId}:`,
          error,
        );
      }
      return { isDeclined: false, declineComment: "", declinedBy: "" };
    },
    [],
  );

  const fetchApprovalCount = useCallback(
    async (rpc: JsonRpc, marketId: number): Promise<number> => {
      try {
        const contractName =
          process.env.REACT_APP_CONTRACT_NAME || "xpredict";
        const result = await rpc.get_table_rows({
          json: true,
          code: contractName,
          scope: marketId.toString(),
          table: "mktapproval",
          limit: 100,
        });
        return result.rows.length;
      } catch (error) {
        console.error(
          `Error fetching approvals for market ${marketId}:`,
          error,
        );
        return 0;
      }
    },
    [],
  );

  const fetchMyProposals = useCallback(async () => {
    if (!session) return;

    setLoading(true);
    try {
      const rpc = new JsonRpc(
        process.env.REACT_APP_RPC_ENDPOINT ||
          process.env.REACT_APP_PROTON_ENDPOINT ||
          "https://proton.eosusa.io",
      );
      const contractName = process.env.REACT_APP_CONTRACT_NAME || "xpredict";
      const currentUser = session.auth.actor.toString();

      const result = await rpc.get_table_rows({
        json: true,
        code: contractName,
        scope: contractName,
        table: "markets3",
        limit: 1000,
      });

      // Filter to only show markets created by this user
      const userRows = result.rows.filter(
        (row: any) => row.suggested_by === currentUser,
      );

      // Fetch approval counts and decline records for each proposal
      const userProposals: MarketProposal[] = [];
      for (const row of userRows) {
        const approvalCount = await fetchApprovalCount(rpc, row.id);
        const declineInfo = await fetchDeclineRecord(rpc, row.id);
        const outcomeNames = await fetchOutcomeNamesForMarket(
          rpc,
          contractName,
          row.id,
        );
        userProposals.push({
          id: row.id,
          question: row.question,
          category: row.category,
          image_url: row.image_url || "",
          expireSec: normalizeTimestamp(row.expire),
          suggested_by: row.suggested_by,
          isLive: row.isLive || false,
          resolved: row.resolved || false,
          outcomes: outcomeNames,
          outcomes_count: row.outcomes_count || 2,
          approvalCount,
          isDeclined: declineInfo.isDeclined,
          declineComment: declineInfo.declineComment,
          declinedBy: declineInfo.declinedBy,
        });
      }

      // Fetch metadata for each proposal
      for (const proposal of userProposals) {
        try {
          const metaResponse = await fetch(
            `/api/market_meta.php?market_id=${proposal.id}`,
          );
          if (metaResponse.ok) {
            const metaData = await metaResponse.json();
            if (metaData.success && metaData.data) {
              proposal.description = metaData.data.description || "";
              proposal.resolution_criteria =
                metaData.data.resolution_criteria || "";
            }
          }
        } catch (e) {
          // Metadata fetch failure is non-critical
        }
      }

      setProposals(userProposals);
    } catch (error) {
      console.error("Error fetching proposals:", error);
      showToast("Failed to load proposals", "error");
    } finally {
      setLoading(false);
    }
  }, [session, fetchDeclineRecord, fetchApprovalCount]);

  useEffect(() => {
    fetchMyProposals();
  }, [fetchMyProposals]);

  const fetchCardComments = useCallback(async (marketIds: number[]) => {
    const newCardComments: Record<number, Comment[]> = {};
    for (const id of marketIds) {
      try {
        const response = await fetch(`/api/comments.php?market_id=${id}`);
        const data = await response.json();
        if (data.success) {
          newCardComments[id] = data.comments || [];
        }
      } catch {
        newCardComments[id] = [];
      }
    }
    setCardComments(newCardComments);
  }, []);

  useEffect(() => {
    if (proposals.length > 0) {
      fetchCardComments(proposals.map((p) => p.id));
    }
  }, [proposals, fetchCardComments]);

  // Fetch full comments for expanded market
  const fetchComments = useCallback(async (marketId: number) => {
    try {
      const response = await fetch(`/api/comments.php?market_id=${marketId}`);
      const data = await response.json();
      if (data.success) {
        setComments(data.comments || []);
        if (data.admins) {
          setCommentAdmins(data.admins);
        }
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  }, []);

  const fetchCommentReactionsData = useCallback(
    async (marketId: number) => {
      try {
        let url = `/api/reactions.php?market_id=${marketId}`;
        if (session) {
          url += `&user_account=${session.auth.actor.toString()}`;
        }
        const response = await fetch(url);
        const data = await response.json();
        if (data.success) {
          setCommentReactions(data.reactions_by_comment || {});
          setUserCommentReactions(data.user_reactions || {});
        }
      } catch (error) {
        console.error("Error fetching comment reactions:", error);
      }
    },
    [session],
  );

  // Auto-fetch comments when detail view is open
  useEffect(() => {
    if (selectedProposalId) {
      fetchComments(selectedProposalId);
      fetchCommentReactionsData(selectedProposalId);
    }
  }, [selectedProposalId, fetchComments, fetchCommentReactionsData]);

  const handleViewDetail = (marketId: number) => {
    if (onNavigateToDetail) {
      onNavigateToDetail(marketId);
      return;
    }
    setSelectedProposalId(marketId);
    setNewComment("");
    setReplyingTo(null);
    setEditingCommentId(null);
    fetchComments(marketId);
    fetchCommentReactionsData(marketId);
  };

  const handleBackToList = () => {
    if (onBackToList) {
      onBackToList();
      return;
    }
    setSelectedProposalId(null);
    setComments([]);
    setNewComment("");
    setReplyingTo(null);
    setEditingCommentId(null);
  };

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session || !selectedProposalId) return;

    if (!newComment.trim()) {
      showToast("Please enter a comment", "error");
      return;
    }

    if (newComment.length > 500) {
      showToast("Comment is too long (max 500 characters)", "error");
      return;
    }

    const proposal = proposals.find((p) => p.id === selectedProposalId);

    setCommentLoading(true);
    try {
      const response = await fetch("/api/comments.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          market_id: selectedProposalId,
          user_account: session.auth.actor.toString(),
          comment_text: newComment.trim(),
          parent_comment_id: replyingTo ? replyingTo.id : null,
          market_creator: proposal?.suggested_by || "",
        }),
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        showToast("Failed to post comment: Server error", "error");
        return;
      }

      if (data.success) {
        setNewComment("");
        setReplyingTo(null);
        fetchComments(selectedProposalId);
        fetchCardComments(proposals.map((p) => p.id));
        showToast("Comment posted!");
      } else {
        showToast(
          "Failed to post comment: " + (data.error || "Unknown error"),
          "error",
        );
      }
    } catch (error) {
      console.error("Error posting comment:", error);
      showToast("Failed to post comment", "error");
    } finally {
      setCommentLoading(false);
    }
  };

  const handleReply = (comment: Comment) => {
    if (!session) {
      showToast("Please connect your wallet to reply", "error");
      return;
    }
    setReplyingTo(comment);
    setNewComment("");
    setTimeout(() => {
      const textarea = document.querySelector(
        ".my-proposals-comment-form textarea",
      ) as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
      }
    }, 100);
  };

  const handleEditComment = async (commentId: number) => {
    if (!session || !selectedProposalId) return;

    const trimmed = editCommentText.trim();
    if (!trimmed) {
      showToast("Comment text is required", "error");
      return;
    }
    if (trimmed.length > 500) {
      showToast("Comment is too long (max 500 characters)", "error");
      return;
    }

    setEditCommentLoading(true);
    try {
      const response = await fetch("/api/comments.php", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          comment_id: commentId,
          user_account: session.auth.actor.toString(),
          comment_text: trimmed,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setEditingCommentId(null);
        setEditCommentText("");
        fetchComments(selectedProposalId);
        fetchCardComments(proposals.map((p) => p.id));
        showToast("Comment updated!");
      } else {
        showToast(
          "Failed to edit comment: " + (data.error || "Unknown error"),
          "error",
        );
      }
    } catch (error) {
      console.error("Error editing comment:", error);
      showToast("Failed to edit comment", "error");
    } finally {
      setEditCommentLoading(false);
    }
  };

  const startEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.comment_text);
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditCommentText("");
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!session || !selectedProposalId) return;
    const userAccount = session.auth.actor.toString();
    if (!commentAdmins.includes(userAccount)) {
      showToast("You do not have permission to delete comments", "error");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;

    try {
      const response = await fetch("/api/comments.php", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          comment_id: commentId,
          user_account: userAccount,
        }),
      });
      const data = await response.json();
      if (data.success) {
        fetchComments(selectedProposalId);
        fetchCardComments(proposals.map((p) => p.id));
      } else {
        showToast("Failed to delete comment", "error");
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      showToast("Failed to delete comment", "error");
    }
  };

  const renderCommentText = (comment: Comment) => {
    if (comment.is_deleted) {
      return (
        <div className="comment-text deleted-text">[Moderator Deleted]</div>
      );
    }
    const lines = comment.comment_text.split("\n");
    if (
      comment.parent_comment_id &&
      lines.length > 0 &&
      lines[0].startsWith("@")
    ) {
      const quotedAuthor = lines[0];
      const quotedText = lines.slice(1).join("\n").trim();
      const replyText = quotedText.split("\n\n").slice(1).join("\n\n");
      const quote = quotedText.split("\n\n")[0];
      return (
        <div className="comment-text">
          {quote && (
            <div className="quoted-message">
              <div className="quote-author">{quotedAuthor}</div>
              <div className="quote-text">{quote}</div>
            </div>
          )}
          {replyText && <div className="reply-text">{replyText}</div>}
        </div>
      );
    }
    return <div className="comment-text">{comment.comment_text}</div>;
  };

  const handleStartEdit = async (proposal: MarketProposal) => {
    setEditingId(proposal.id);
    setEditQuestion(proposal.question);
    setEditCategory(proposal.category);
    setEditImageUrl(proposal.image_url);
    setEditImagePreview("");
    setEditImageFile(null);
    setEditDescription(proposal.description || "");
    setEditResolutionCriteria(proposal.resolution_criteria || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditQuestion("");
    setEditCategory("");
    setEditDescription("");
    setEditResolutionCriteria("");
    setEditImageUrl("");
    setEditImageFile(null);
    setEditImagePreview("");
  };

  const handleEditImageFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      showToast("Only JPG, PNG, and WebP images are allowed", "error");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showToast("Image must be less than 2MB", "error");
      return;
    }

    setEditImageFile(file);
    setEditImagePreview(URL.createObjectURL(file));
  };

  const handleEditUploadImage = async () => {
    if (!editImageFile) return;

    setEditUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", editImageFile);

      const response = await fetch("/api/upload.php", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      setEditImageUrl(data.url);
      showToast("Image uploaded successfully!");
    } catch (error) {
      console.error("Error uploading image:", error);
      showToast("Failed to upload image: " + error, "error");
    } finally {
      setEditUploadingImage(false);
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session || editingId === null || !editQuestion || !editCategory) {
      showToast("Please fill in all required fields", "error");
      return;
    }

    setEditLoading(true);
    try {
      // Auto-upload image if a file was selected
      let finalImageUrl = editImageUrl;
      if (editImageFile) {
        setEditUploadingImage(true);
        try {
          const formData = new FormData();
          formData.append("file", editImageFile);

          const response = await fetch("/api/upload.php", {
            method: "POST",
            body: formData,
          });

          if (!response.ok) {
            throw new Error("Upload failed");
          }

          const data = await response.json();
          finalImageUrl = data.url;
          setEditImageUrl(data.url);
        } catch (uploadError) {
          console.error("Error uploading image:", uploadError);
          showToast("Failed to upload image: " + uploadError, "error");
          setEditLoading(false);
          setEditUploadingImage(false);
          return;
        } finally {
          setEditUploadingImage(false);
        }
      }

      const originalProposal = proposals.find(
        (proposal) => proposal.id === editingId,
      );
      if (!originalProposal) {
        throw new Error("Selected proposal could not be loaded");
      }

      // if (editQuestion !== originalProposal.question) {
      //   payload.question = editQuestion;
      // }
      // payload.category =
      //   editCategory !== originalProposal.category ? editCategory : "";
      // payload.image_url =
      //   finalImageUrl !== originalProposal.image_url ? finalImageUrl : "";

      // await editMarketTransaction(session, payload);

      const hasQuestionChanged =
        editQuestion !== originalProposal.question;

      const hasCategoryChanged =
        editCategory !== originalProposal.category;

      const hasImageChanged =
        finalImageUrl !== originalProposal.image_url;

      const hasChanges =
        hasQuestionChanged ||
        hasCategoryChanged ||
        hasImageChanged;

      if (hasChanges) {
        const payload: Parameters<typeof editMarketTransaction>[1] = {
          market_id: editingId,
          expireTime: originalProposal.expireSec,
          outcomes: originalProposal.outcomes,
        };

        payload.question = hasQuestionChanged
            ? editQuestion
            : originalProposal.question;

          payload.category = hasCategoryChanged
            ? editCategory
            : originalProposal.category;

          payload.image_url = hasImageChanged
            ? finalImageUrl
            : originalProposal.image_url;

        await editMarketTransaction(session, payload);
      }

      // Save description and resolution criteria to market_meta API
      if (editDescription || editResolutionCriteria) {
        try {
          await fetch("/api/market_meta.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              market_id: editingId,
              description: editDescription,
              resolution_criteria: editResolutionCriteria,
            }),
          });
        } catch (metaError) {
          console.error("Error saving market meta:", metaError);
        }
      }

      showToast("Proposal updated successfully!");
      handleCancelEdit();
      fetchMyProposals();
    } catch (error) {
      console.error("Error updating proposal:", error);
      showToast("Failed to update proposal: " + error, "error");
    } finally {
      setEditLoading(false);
    }
  };

  const getStatusLabel = (proposal: MarketProposal) => {
    if (proposal.resolved) return "Resolved";
    if (proposal.isLive) return "Live";
    if (proposal.isDeclined) return "Rejected";
    return "Pending Approval";
  };

  const getStatusClass = (proposal: MarketProposal) => {
    if (proposal.resolved) return "status-resolved";
    if (proposal.isLive) return "status-live";
    if (proposal.isDeclined) return "status-rejected";
    return "status-pending";
  };

  const currentUser = session?.auth?.actor?.toString() || "";

  if (!session) {
    return (
      <div className="my-proposals">
        <h2>My Prediction Proposals</h2>
        <p className="empty-state">
          Please connect your wallet to view your proposals.
        </p>
      </div>
    );
  }

  const renderCommentSection = (proposal: MarketProposal) => {
    return (
      <div className="community-comments-section">
        <h4>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="18"
            height="18"
          >
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          Discussion ({comments.filter((c) => !c.is_deleted).length})
        </h4>

        {/* Comment form */}
        <form
          onSubmit={handlePostComment}
          className="community-comment-form my-proposals-comment-form"
        >
          {replyingTo && (
            <div className="community-replying-banner">
              <span>
                Replying to <strong>{replyingTo.user_account}</strong>
              </span>
              <button
                type="button"
                onClick={() => {
                  setReplyingTo(null);
                  setNewComment("");
                }}
              >
                Cancel
              </button>
            </div>
          )}
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={
              replyingTo
                ? `Write your reply to ${replyingTo.user_account}...`
                : "Share your thoughts on this proposal..."
            }
            maxLength={500}
            rows={3}
            disabled={commentLoading}
          />
          <div className="community-comment-form-footer">
            <span className="community-char-count">
              {newComment.length}/500
            </span>
            <button
              type="submit"
              disabled={commentLoading || !newComment.trim()}
            >
              {commentLoading
                ? "Posting..."
                : replyingTo
                  ? "Post Reply"
                  : "Post Comment"}
            </button>
          </div>
        </form>

        {/* Comments list */}
        <div className="community-comments-list">
          {comments.filter((c) => !c.parent_comment_id).length === 0 ? (
            <p className="community-no-comments">
              No comments yet. Be the first to share your thoughts!
            </p>
          ) : (
            comments
              .filter((c) => !c.parent_comment_id)
              .sort((a, b) => b.created_at - a.created_at)
              .map((comment) => (
                <div key={comment.id} className="community-comment-thread">
                  <div
                    className={`community-comment-card ${comment.is_deleted ? "deleted" : ""}`}
                  >
                    <div className="community-comment-header">
                      <span className="community-comment-author">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          width="14"
                          height="14"
                        >
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        {comment.user_account}
                        {comment.user_account === proposal.suggested_by && (
                          <span className="community-comment-creator-badge">
                            Creator
                          </span>
                        )}
                      </span>
                      <span className="community-comment-time">
                        {new Date(comment.created_at * 1000).toLocaleString()}
                        {comment.updated_at && (
                          <span className="community-comment-edited">
                            (edited)
                          </span>
                        )}
                      </span>
                    </div>
                    {editingCommentId === comment.id ? (
                      <div className="community-edit-comment-form">
                        <textarea
                          value={editCommentText}
                          onChange={(e) => setEditCommentText(e.target.value)}
                          maxLength={500}
                          rows={3}
                          disabled={editCommentLoading}
                        />
                        <div className="community-edit-comment-actions">
                          <span className="community-char-count">
                            {editCommentText.length}/500
                          </span>
                          <button
                            onClick={cancelEditComment}
                            disabled={editCommentLoading}
                            className="community-edit-cancel-btn"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleEditComment(comment.id)}
                            disabled={
                              editCommentLoading || !editCommentText.trim()
                            }
                            className="community-edit-save-btn"
                          >
                            {editCommentLoading ? "Saving..." : "Save"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      renderCommentText(comment)
                    )}
                    {!comment.is_deleted && editingCommentId !== comment.id && (
                      <div className="community-comment-actions">
                        <button
                          onClick={() => handleReply(comment)}
                          className="community-reply-btn"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            width="12"
                            height="12"
                          >
                            <path d="M3 10l7-7v4c11 0 11 9 11 9s-2-5-11-5v4L3 10z" />
                          </svg>
                          Reply
                        </button>
                        {session && comment.user_account === currentUser && (
                          <button
                            onClick={() => startEditComment(comment)}
                            className="community-edit-btn"
                          >
                            Edit
                          </button>
                        )}
                        {session && commentAdmins.includes(currentUser) && (
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="community-delete-btn"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                    <CommentReactions
                      commentId={comment.id}
                      marketId={proposal.id}
                      session={session}
                      reactions={
                        commentReactions[comment.id] || DEFAULT_REACTIONS
                      }
                      userReaction={userCommentReactions[comment.id] || null}
                      onReactionChange={() =>
                        fetchCommentReactionsData(proposal.id)
                      }
                    />
                  </div>

                  {/* Nested replies */}
                  {comments
                    .filter((r) => r.parent_comment_id === comment.id)
                    .sort((a, b) => a.created_at - b.created_at)
                    .map((reply) => (
                      <div
                        key={reply.id}
                        className={`community-comment-card community-comment-reply ${reply.is_deleted ? "deleted" : ""}`}
                      >
                        <div className="community-comment-header">
                          <span className="community-comment-author">
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              width="14"
                              height="14"
                            >
                              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                            {reply.user_account}
                            {reply.user_account === proposal.suggested_by && (
                              <span className="community-comment-creator-badge">
                                Creator
                              </span>
                            )}
                          </span>
                          <span className="community-comment-time">
                            {new Date(reply.created_at * 1000).toLocaleString()}
                            {reply.updated_at && (
                              <span className="community-comment-edited">
                                (edited)
                              </span>
                            )}
                          </span>
                        </div>
                        {editingCommentId === reply.id ? (
                          <div className="community-edit-comment-form">
                            <textarea
                              value={editCommentText}
                              onChange={(e) =>
                                setEditCommentText(e.target.value)
                              }
                              maxLength={500}
                              rows={3}
                              disabled={editCommentLoading}
                            />
                            <div className="community-edit-comment-actions">
                              <span className="community-char-count">
                                {editCommentText.length}/500
                              </span>
                              <button
                                onClick={cancelEditComment}
                                disabled={editCommentLoading}
                                className="community-edit-cancel-btn"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleEditComment(reply.id)}
                                disabled={
                                  editCommentLoading || !editCommentText.trim()
                                }
                                className="community-edit-save-btn"
                              >
                                {editCommentLoading ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          renderCommentText(reply)
                        )}
                        {!reply.is_deleted && editingCommentId !== reply.id && (
                          <div className="community-comment-actions">
                            {session && reply.user_account === currentUser && (
                              <button
                                onClick={() => startEditComment(reply)}
                                className="community-edit-btn"
                              >
                                Edit
                              </button>
                            )}
                            {session && commentAdmins.includes(currentUser) && (
                              <button
                                onClick={() => handleDeleteComment(reply.id)}
                                className="community-delete-btn"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                        <CommentReactions
                          commentId={reply.id}
                          marketId={proposal.id}
                          session={session}
                          reactions={
                            commentReactions[reply.id] || DEFAULT_REACTIONS
                          }
                          userReaction={userCommentReactions[reply.id] || null}
                          onReactionChange={() =>
                            fetchCommentReactionsData(proposal.id)
                          }
                        />
                      </div>
                    ))}
                </div>
              ))
          )}
        </div>
      </div>
    );
  };

  // ===== Detail View =====
  if (selectedProposalId !== null) {
    const proposal = proposals.find((p) => p.id === selectedProposalId);

    return (
      <div className="my-proposals">
        <button className="community-back-btn" onClick={handleBackToList}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="16"
            height="16"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to My Proposals
        </button>

        {loading && !proposal ? (
          <div className="admin-section">
            <Loader
              label="Loading proposal details..."
              className="app-loader-compact"
            />
          </div>
        ) : proposal ? (
          <div className="community-detail">
            <div className="community-detail-header">
              <div className="community-detail-title-row">
                <span className="proposal-id">#{proposal.id}</span>
                <span className={`proposal-status ${getStatusClass(proposal)}`}>
                  {getStatusLabel(proposal)}
                </span>
              </div>
              <h2 className="community-detail-question">{proposal.question}</h2>
            </div>

            {proposal.image_url && (
              <div className="community-detail-image">
                <img src={proposal.image_url} alt="Market" />
              </div>
            )}

            <div className="community-detail-info">
              <div className="community-detail-grid">
                <div className="community-detail-field">
                  <span className="community-detail-label">Category</span>
                  <span className="community-detail-value">
                    {proposal.category}
                  </span>
                </div>
                <div className="community-detail-field">
                  <span className="community-detail-label">Expires</span>
                  <span className="community-detail-value">
                    {formatDate(proposal.expireSec, true)}
                  </span>
                </div>
                <div className="community-detail-field">
                  <span className="community-detail-label">Proposed by</span>
                  <span className="community-detail-value">
                    {proposal.suggested_by}
                  </span>
                </div>
                <div className="community-detail-field">
                  <span className="community-detail-label">Status</span>
                  <span className="community-detail-value">
                    {proposal.isLive
                      ? "Live"
                      : proposal.resolved
                        ? "Resolved"
                        : proposal.isDeclined
                          ? "Rejected"
                          : "Pending Approval"}
                  </span>
                </div>
                <div className="community-detail-field">
                  <span className="community-detail-label">Market Type</span>
                  <span className="community-detail-value">
                    {proposal.outcomes_count === 2
                      ? "Binary (Yes/No)"
                      : `Multi-Outcome (${proposal.outcomes_count} outcomes)`}
                  </span>
                </div>
                <div className="community-detail-field">
                  <span className="community-detail-label">
                    Interest of Community
                  </span>
                  <span className="community-detail-value">
                    {proposal.approvalCount} vote
                    {proposal.approvalCount !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              {/* Outcomes Section */}
              {proposal.outcomes && proposal.outcomes.length > 0 && (
                <div className="community-detail-section">
                  <h4>Outcomes ({proposal.outcomes.length})</h4>
                  <div className="community-detail-outcomes-list">
                    {proposal.outcomes.map((outcome, idx) => (
                      <div key={idx} className="community-detail-outcome-item">
                        <span className="community-detail-outcome-number">
                          {idx + 1}
                        </span>
                        <span className="community-detail-outcome-name">
                          {outcome}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rejection Reason Section */}
              {proposal.isDeclined && proposal.declineComment && (
                <div
                  className="community-detail-section"
                  style={{
                    background: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "8px",
                    padding: "1rem",
                    marginBottom: "1rem",
                  }}
                >
                  <h4 style={{ color: "#ef4444", margin: "0 0 0.5rem 0" }}>
                    Rejection Reason
                  </h4>
                  <p
                    className="community-detail-text"
                    style={{ color: "#fca5a5" }}
                  >
                    {proposal.declineComment}
                  </p>
                  {proposal.declinedBy && (
                    <p
                      style={{
                        color: "#94a3b8",
                        fontSize: "0.85rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      Declined by: {proposal.declinedBy}
                    </p>
                  )}
                </div>
              )}

              {/* Description Section */}
              <div className="community-detail-section">
                <h4>Description</h4>
                {proposal.description ? (
                  <p className="community-detail-text">
                    {proposal.description}
                  </p>
                ) : (
                  <p className="community-detail-text community-detail-empty">
                    No description provided
                  </p>
                )}
              </div>

              {/* Resolution Criteria Section */}
              <div className="community-detail-section">
                <h4>Resolution Criteria</h4>
                {proposal.resolution_criteria ? (
                  <p className="community-detail-text">
                    {proposal.resolution_criteria}
                  </p>
                ) : (
                  <p className="community-detail-text community-detail-empty">
                    No resolution criteria provided
                  </p>
                )}
              </div>
            </div>

            {/* Edit proposal button */}
            {!proposal.isLive && !proposal.resolved && (
              <div className="proposal-actions" style={{ marginTop: "1rem" }}>
                <button
                  className="edit-proposal-btn"
                  onClick={() => {
                    handleBackToList();
                    setTimeout(() => {
                      handleStartEdit(proposal);
                    }, 100);
                  }}
                >
                  Edit Proposal
                </button>
              </div>
            )}

            {/* Comment Section */}
            {renderCommentSection(proposal)}
          </div>
        ) : (
          <div className="admin-section">
            <p
              style={{
                color: "#94a3b8",
                textAlign: "center",
                padding: "2rem 0",
              }}
            >
              Proposal not found.
            </p>
          </div>
        )}

        {/* Toast notification */}
        {toast && (
          <div className={`toast-notification ${toast.type}`}>
            <span className="toast-message">{toast.message}</span>
            <button className="toast-close" onClick={() => setToast(null)}>
              &times;
            </button>
          </div>
        )}
      </div>
    );
  }

  // ===== List View =====
  return (
    <div className="my-proposals">
      <h2>My Prediction Proposals</h2>
      <p className="proposals-subtitle">
        View and manage your submitted market proposals. You can edit proposals
        that haven't gone live yet.
      </p>

      {loading ? (
        <Loader
          label="Loading your proposals..."
          className="app-loader-compact"
        />
      ) : proposals.length === 0 ? (
        <p className="empty-state">
          You haven't submitted any market proposals yet.
        </p>
      ) : (
        <div className="proposals-list">
          {proposals.map((proposal) => (
            <div key={proposal.id} className="proposal-card">
              {editingId === proposal.id ? (
                <form
                  onSubmit={handleSaveEdit}
                  className="proposal-edit-form admin-form"
                >
                  <div className="proposal-edit-header">
                    <h4>Editing Proposal #{proposal.id}</h4>
                    <button
                      type="button"
                      className="cancel-edit-btn"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="form-group">
                    <label>Question</label>
                    <input
                      type="text"
                      value={editQuestion}
                      onChange={(e) => setEditQuestion(e.target.value)}
                      placeholder="Market question"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Category</label>
                    <select
                      value={editCategory}
                      onChange={(e) => setEditCategory(e.target.value)}
                      required
                    >
                      <option value="">Select category</option>
                      <option value="Crypto">Crypto</option>
                      <option value="Politics">Politics</option>
                      <option value="Sports">Sports</option>
                      <option value="Technology">Technology</option>
                      <option value="Finance">Finance</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Description (Optional)</label>
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Provide additional context or details about this market..."
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Resolution Criteria</label>
                    <textarea
                      value={editResolutionCriteria}
                      onChange={(e) =>
                        setEditResolutionCriteria(e.target.value)
                      }
                      placeholder="Please enter very detailed rules, dates, and a description on how the result is determined when the market is resolved."
                      rows={4}
                    />
                  </div>

                  <div className="form-group">
                    <label>Market Image</label>
                    <div className="image-upload-section">
                      <input
                        type="text"
                        value={editImageUrl}
                        onChange={(e) => setEditImageUrl(e.target.value)}
                        placeholder="Paste image URL or upload below"
                      />
                      <div className="upload-controls">
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={handleEditImageFileChange}
                          id={`proposal-edit-image-${proposal.id}`}
                          style={{ display: "none" }}
                        />
                        <label
                          htmlFor={`proposal-edit-image-${proposal.id}`}
                          className="upload-button"
                        >
                          Choose File
                        </label>
                        {editImageFile && (
                          <button
                            type="button"
                            onClick={handleEditUploadImage}
                            disabled={editUploadingImage}
                            className="upload-submit-button"
                          >
                            {editUploadingImage ? "Uploading..." : "Upload"}
                          </button>
                        )}
                      </div>
                      {editImagePreview && (
                        <div className="image-preview image-upload-preview">
                          <img src={editImagePreview} alt="Preview" />
                        </div>
                      )}
                      {editImageUrl && !editImagePreview && (
                        <div className="image-preview image-upload-preview">
                          <img src={editImageUrl} alt="Market" />
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={editLoading}
                    className="submit-button"
                  >
                    {editLoading ? "Updating..." : "Save Changes"}
                  </button>
                </form>
              ) : (
                <>
                  <div className="proposal-card-header">
                    <span
                      className={`proposal-status ${getStatusClass(proposal)}`}
                    >
                      {getStatusLabel(proposal)}
                    </span>
                    <span className="proposal-id">#{proposal.id}</span>
                  </div>
                  {proposal.image_url && (
                    <div
                      className="proposal-image"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleViewDetail(proposal.id)}
                    >
                      <img src={proposal.image_url} alt="Market" />
                    </div>
                  )}
                  <h4
                    className="proposal-question"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleViewDetail(proposal.id)}
                  >
                    {proposal.question}
                  </h4>
                  <div className="proposal-details">
                    <p>
                      <strong>Category:</strong> {proposal.category}
                    </p>
                    <p>
                      <strong>Expires:</strong>{" "}
                      {formatDate(proposal.expireSec, true)}
                    </p>
                    <p>
                      <strong>Interest of Community:</strong>{" "}
                      {proposal.approvalCount} vote
                      {proposal.approvalCount !== 1 ? "s" : ""}
                    </p>
                    {proposal.outcomes && proposal.outcomes.length > 0 && (
                      <p>
                        <strong>Outcomes:</strong>{" "}
                        {proposal.outcomes.join(", ")}
                      </p>
                    )}
                    {proposal.description && (
                      <p>
                        <strong>Description:</strong>{" "}
                        {proposal.description.substring(0, 150)}
                        {proposal.description.length > 150 ? "..." : ""}
                      </p>
                    )}
                    {proposal.resolution_criteria && (
                      <p>
                        <strong>Resolution Criteria:</strong>{" "}
                        {proposal.resolution_criteria.substring(0, 150)}
                        {proposal.resolution_criteria.length > 150 ? "..." : ""}
                      </p>
                    )}
                    {proposal.isDeclined && proposal.declineComment && (
                      <div
                        style={{
                          background: "rgba(239, 68, 68, 0.1)",
                          border: "1px solid rgba(239, 68, 68, 0.3)",
                          borderRadius: "6px",
                          padding: "0.75rem",
                          marginTop: "0.5rem",
                        }}
                      >
                        <p
                          style={{
                            color: "#ef4444",
                            fontWeight: 600,
                            margin: "0 0 0.25rem 0",
                            fontSize: "0.85rem",
                          }}
                        >
                          Rejection Reason:
                        </p>
                        <p
                          style={{
                            color: "#fca5a5",
                            margin: 0,
                            fontSize: "0.85rem",
                          }}
                        >
                          {proposal.declineComment.length > 200
                            ? proposal.declineComment.substring(0, 200) + "..."
                            : proposal.declineComment}
                        </p>
                      </div>
                    )}
                  </div>
                  {!proposal.isLive &&
                    !proposal.resolved &&
                    !proposal.isDeclined && (
                      <div className="proposal-actions">
                        <button
                          className="edit-proposal-btn"
                          onClick={() => handleStartEdit(proposal)}
                        >
                          Edit Proposal
                        </button>
                      </div>
                    )}

                  {/* Recent comments preview */}
                  {(() => {
                    const marketComments = (
                      cardComments[proposal.id] || []
                    ).filter((c) => !c.is_deleted);
                    const totalCount = marketComments.length;
                    const recentComments = marketComments
                      .sort((a, b) => b.created_at - a.created_at)
                      .slice(0, 3);
                    return (
                      <div
                        className="my-proposals-comments-section"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleViewDetail(proposal.id)}
                      >
                        <div className="my-proposals-comments-header">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            width="14"
                            height="14"
                          >
                            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                          </svg>
                          <span>
                            {totalCount} comment{totalCount !== 1 ? "s" : ""}
                          </span>
                        </div>
                        {recentComments.length > 0 ? (
                          <>
                            {recentComments.map((c) => (
                              <div
                                key={c.id}
                                className="my-proposals-comment-preview"
                              >
                                <span className="my-proposals-comment-author">
                                  {c.user_account}
                                </span>
                                <span className="my-proposals-comment-text">
                                  {c.comment_text.length > 100
                                    ? c.comment_text.substring(0, 100) + "..."
                                    : c.comment_text}
                                </span>
                              </div>
                            ))}
                            <span className="my-proposals-view-all-btn">
                              View all {totalCount} comments
                            </span>
                          </>
                        ) : (
                          <p className="my-proposals-no-comments">
                            No comments yet
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-message">{toast.message}</span>
          <button className="toast-close" onClick={() => setToast(null)}>
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default MyProposals;
